// The rooms (30-day rows 30 and 31). A person stands in one room: Jobs or Bids (then one job or bid), or a room a
// module brings (Service, Warehouse), or the company's. The shell says which on every screen, offers only that
// room's things, and each room has a front door. The rooms themselves come from the manifests; the shell holds no
// module ids. Nothing about the records changes: a bid and a job are still one document.
import { useEffect, useState } from 'react';
import { roomsFor, type RoomDef } from '@tradeview/core';
import { useAuth } from '../auth';
import { useProjects, rememberProject, rememberedProject, type Project } from './projects';

/** Fired on window whenever the remembered room or project changes, so the shell follows a picker in any module. */
export const ROOM_EVENT = 'tv:project';
const ROOM_KEY = 'tv.room';
/** The remembered room id; '' means "the room of the remembered project" (a job or a bid). */
export function rememberedRoom(): string { try { return localStorage.getItem(ROOM_KEY) || ''; } catch { return ''; } }
export function rememberRoom(id: string) { try { localStorage.setItem(ROOM_KEY, id); window.dispatchEvent(new Event(ROOM_EVENT)); } catch { /* fine */ } }

export function useRoom() {
  const { session } = useAuth();
  const rooms = roomsFor(session?.role ?? null, session?.entitlements ?? null);
  const picksBids = rooms.some(r => r.picks === 'bid');
  const projects = useProjects({ bids: picksBids });
  const [tick, setTick] = useState(0);
  useEffect(() => { const h = () => setTick(t => t + 1); window.addEventListener(ROOM_EVENT, h); return () => window.removeEventListener(ROOM_EVENT, h); }, []);
  void tick;
  const list = projects.data ?? [];
  const project = list.find(p => p.id === rememberedProject()) ?? null;
  const asked = rememberedRoom();
  // A remembered room id wins (a single room, or Jobs / Bids asked for by a room-wide page like its schedule); otherwise
  // the room is the remembered project's kind. In a room of projects the picked project counts only when it is of that kind.
  const askedRoom = asked ? rooms.find(r => r.id === asked) ?? null : null;
  const projectRoom: RoomDef | null = project ? (rooms.find(r => r.picks === (project.bid ? 'bid' : 'job')) ?? null) : null;
  const room: RoomDef | null = askedRoom ?? projectRoom;
  const roomProject = room?.picks && project && (project.bid ? 'bid' : 'job') === room.picks ? project : null;
  return {
    rooms, room, project: roomProject, projects: list, loading: projects.isLoading,
    /** Enter a room: a single room by id, a room of projects by picking one. */
    enterRoom: (id: string) => rememberRoom(id),
    enterProject: (pid: string) => { rememberRoom(''); rememberProject(pid); },
  };
}

/** Where a room opens: its door, with the picked project in place of :id. */
export const doorOf = (room: RoomDef, p?: Pick<Project, 'id'> | null) => room.door.replace(':id', p?.id ?? '');
/** A project's own front door, whatever room it is in. */
export const frontDoor = (p: Pick<Project, 'id' | 'bid'>) => p.bid ? `/bids/${p.id}` : `/job/${p.id}`;
